import React, { useState } from "react";
import PersonForm from "./components/PersonForm";
import { Person, FamilyContext, FormTabType } from "./types";
import { getLabelsByLevel } from "./utils/personUtils";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { isValidCPF, isValidEmail, isValidPhone } from "@/utils/maskUtils";

interface PersonFormModularProps {
  person: Person | Person[];
  onChange: (person: Person | Person[]) => void;
  label?: string;
  description?: string;
  handleNextStep?: () => void;
  handlePreviousStep?: () => void;
  activeStep?: number;
  totalSteps?: number;
  multiline?: boolean;
}

// Função para validar campos específicos
const validateField = (field: keyof Person, value: string | undefined): boolean => {
  if (!value) return false;
  
  switch (field) {
    case 'name':
      // Nome deve ter pelo menos 3 caracteres
      return value.trim().length >= 3;
    case 'email':
      return isValidEmail(value);
    case 'cpf':
      return isValidCPF(value);
    case 'celphone':
      return isValidPhone(value);
    default:
      return true;
  }
};

export const PersonFormModular: React.FC<PersonFormModularProps> = ({
  person,
  onChange,
  label = "Pessoa",
  description = "",
  handleNextStep,
  handlePreviousStep,
  activeStep = 1,
  totalSteps = 25,
  multiline = true
}) => {
  // Converter para array se for uma única pessoa
  const personsArray = Array.isArray(person) ? person : [person];
  
  // Estado para rastrear a pessoa raiz ativa
  const [activeRootPerson, setActiveRootPerson] = useState<number>(0);
  
  // Estado para rastrear a navegação na hierarquia familiar
  const [context, setContext] = useState<FamilyContext>({
    level: "MAIN",
    path: [0, 0, 0], // [childIdx, grandChildIdx, greatGrandChildIdx]
    activeTab: "PERSONAL",
    rootPersonIndex: 0,
    childTab: "PERSONAL",
    grandChildTab: "PERSONAL",
    greatGrandChildTab: "PERSONAL"
  });
  
  // Inicializar com pessoas vazias se não houver
  React.useEffect(() => {
    if (multiline) {
      if (!Array.isArray(person) || person.length === 0) {
        onChange([{}]);
      }
    } else {
      if (!person || (typeof person === 'object' && Object.keys(person).length === 0)) {
        onChange({});
      }
    }
  }, []);
  
  // Atualizar contexto familiar
  const updateContext = (newContext: Partial<FamilyContext>) => {
    setContext(prev => ({
      ...prev,
      ...newContext
    }));
    
    // Atualizar o índice da pessoa raiz ativa se necessário
    if (newContext.rootPersonIndex !== undefined) {
      setActiveRootPerson(newContext.rootPersonIndex);
    }
  };

  // Função para adicionar uma nova pessoa raiz
  const handleAddRootPerson = () => {
    if (!multiline) return;
    
    const newPersons = [...personsArray, {}];
    onChange(newPersons);
    
    // Atualizar para a nova pessoa adicionada
    const newIndex = newPersons.length - 1;
    setActiveRootPerson(newIndex);
    updateContext({ rootPersonIndex: newIndex });
  };
  
  // Função para remover uma pessoa raiz
  const handleRemoveRootPerson = () => {
    if (!multiline || personsArray.length <= 1) return;
    
    const newPersons = personsArray.filter((_, idx) => idx !== activeRootPerson);
    onChange(newPersons);
    
    // Ajustar o índice ativo se necessário
    if (activeRootPerson >= newPersons.length) {
      const newIndex = Math.max(0, newPersons.length - 1);
      setActiveRootPerson(newIndex);
      updateContext({ rootPersonIndex: newIndex });
    }
  };
  
  // Função para mudar a pessoa raiz ativa
  const handleChangeRootPerson = (index: number) => {
    if (!multiline || index >= personsArray.length) return;
    
    setActiveRootPerson(index);
    updateContext({ 
      rootPersonIndex: index,
      level: "MAIN",
      path: [0, 0, 0],
      activeTab: "PERSONAL",
      childTab: "PERSONAL",
      grandChildTab: "PERSONAL",
      greatGrandChildTab: "PERSONAL"
    });
  };
  
  // Função para atualizar a pessoa ativa
  const handlePersonChange = (updatedPerson: Person) => {
    if (multiline) {
      const newPersons = [...personsArray];
      newPersons[activeRootPerson] = updatedPerson;
      onChange(newPersons);
    } else {
      onChange(updatedPerson);
    }
  };
  
  // Obter a pessoa ativa atual
  const activePerson = multiline ? personsArray[activeRootPerson] || {} : (person as Person);
  
  // Verificar se os dados são válidos
  const isPersonValid = (p: Person): boolean => {
    if (!p) return false;
    
    // Validar campos básicos
    const hasBasicData = !!(
      p.name && validateField('name', p.name) && 
      p.email && validateField('email', p.email) && 
      p.cpf && validateField('cpf', p.cpf) && 
      p.celphone && validateField('celphone', p.celphone)
    );
    
    if (!hasBasicData) return false;
    
    // Verificar se há representantes legais e se estão completos
    if (p.legalRepresentatives && p.legalRepresentatives.length > 0) {
      const hasValidLegalReps = p.legalRepresentatives.every(repArray => {
        const rep = repArray[0];
        return rep && 
               rep.name && validateField('name', rep.name) && 
               rep.email && validateField('email', rep.email) && 
               rep.cpf && validateField('cpf', rep.cpf) && 
               rep.celphone && validateField('celphone', rep.celphone);
      });
      if (!hasValidLegalReps) return false;
    }
    
    // Verificar o cônjuge apenas se houver algum campo preenchido
    if (p.couple && p.couple[0]) {
      const spouse = p.couple[0];
      const hasAnySpouseField = !!(spouse.name || spouse.email || spouse.cpf || spouse.celphone);
      
      if (hasAnySpouseField) {
        const hasAllSpouseFields = !!(
          spouse.name && validateField('name', spouse.name) && 
          spouse.email && validateField('email', spouse.email) && 
          spouse.cpf && validateField('cpf', spouse.cpf) && 
          spouse.celphone && validateField('celphone', spouse.celphone)
        );
        if (!hasAllSpouseFields) return false;
      }
    }
    
    return true;
  };
  
  const isValid = multiline 
    ? personsArray.length > 0 && personsArray.every(p => isPersonValid(p))
    : isPersonValid(activePerson);

  return (
    <>
      <div className="h-full w-full">
        <div className="h-full pt-5 sm:pt-8">
          <h1 className="text-background font-bold text-sm sm:text-2xl mb-2.5 sm:mb-4">
            {multiline && personsArray.length > 1 
              ? `${label} ${activeRootPerson + 1}` 
              : label}
          </h1>
          {description && <p className="text-grey-light text-sm sm:text-base mb-6 sm:mb-8">{description}</p>}
          
          {/* Navegação entre pessoas raiz - similar ao PersonNavigation */}
          {multiline && (
            <div className="flex flex-col-reverse sm:flex-row justify-center items-center gap-1 sm:gap-4 w-full relative mb-6 pb-4">
              <div className="flex gap-2">
                {personsArray.map((_, index) => (
                  <div 
                    key={index} 
                    onClick={() => handleChangeRootPerson(index)} 
                    className="flex flex-col justify-between items-center w-[35px] h-[49px] cursor-pointer"
                  >
                    <div className={`flex items-center justify-center w-[35px] h-[35px] rounded-md ${index === activeRootPerson ? 'bg-primary' : 'bg-background/10'}`}>
                      <span className="text-background text-xs sm:text-base">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                    </div>
                    
                    {/* Indica a pessoa atual */}
                    {index === activeRootPerson && (
                      <span className="w-full h-[5px] rounded-xs bg-primary" />
                    )}
                  </div>
                ))}
                
                {/* Se não houver pessoas ou apenas uma, mostrar uma mensagem */}
                {personsArray.length === 0 && (
                  <div className="text-background text-sm">
                    Adicione uma pessoa usando o botão ao lado
                  </div>
                )}
              </div>
              
              {/* ADICIONA OU REMOVE PESSOA */}
              <div className="flex self-end gap-2 sm:absolute right-0 top-0">
                {personsArray.length > 1 && (
                  <Button 
                    onClick={handleRemoveRootPerson} 
                    variant="destructive" 
                    className="h-[35px] text-background rounded-md bg-background/10 hover:bg-background/10 my-0 py-0 has-[>svg]:px-2"
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
                <Button 
                  onClick={handleAddRootPerson}
                  className="flex items-center justify-center h-[35px] w-[35px] p-0 bg-primary rounded-md hover:bg-primary/90"
                >
                  <Plus size={16} />
                </Button>
              </div>
            </div>
          )}
          
          {/* Exibir o nome da pessoa selecionada se houver mais de uma */}
          {multiline && personsArray.length > 1 && personsArray[activeRootPerson]?.name && (
            <p className="text-background text-sm sm:text-base mb-4">
              {personsArray[activeRootPerson].name}
            </p>
          )}
        
          {/* Formulário da pessoa */}
          <PersonForm
            person={activePerson}
            onChange={handlePersonChange}
            context={context}
            onContextChange={updateContext}
          />
        </div>
        
        {/* Botões de navegação para desktop */}
        <div className="hidden sm:flex sm:flex-1 sm:items-end sm:justify-between">
          {handlePreviousStep && (
            <Button variant="ghost" className="rounded-md h-9 px-2 bg-grey-light text-grey-dark" onClick={handlePreviousStep}>
              Anterior
            </Button>
          )}
          {handleNextStep && (
            <Button 
              type="button" 
              disabled={!isValid}
              className="rounded-md h-9 px-2" 
              onClick={handleNextStep}
            >
              Próximo
            </Button>
          )}
        </div>
      </div>
      
      {/* Botões de navegação para mobile */}
      {(handleNextStep && handlePreviousStep) && (
        <footer className="w-full sm:hidden flex z-10 gap-4 justify-between items-center">
          <Button 
            variant={activeStep === 1 ? 'ghost' : 'default'} 
            className={`flex-1 rounded-md h-9 px-2 ${activeStep === 1 ? 'bg-grey-light text-grey-dark' : ''}`} 
            onClick={activeStep === 1 ? undefined : handlePreviousStep}
          >
            Anterior
          </Button>
          <Button 
            variant="ghost" 
            className="flex-1 bg-background/10 hover:bg-background/10 cursor-default rounded-md hover:text-grey-light text-grey-light h-9 px-2"
          >
            Etapa {activeStep < 10 ? `0${activeStep}` : activeStep}/{totalSteps}
          </Button>
          <Button 
            variant={activeStep === totalSteps ? 'ghost' : 'default'} 
            disabled={!isValid} 
            className={`flex-1 rounded-md h-9 px-2 ${activeStep === totalSteps ? 'bg-grey-light text-grey-dark' : ''}`} 
            onClick={activeStep === totalSteps ? undefined : handleNextStep}
          >
            Próximoaaa
          </Button>
        </footer>
      )}
    </>
  );
};

export default PersonFormModular; 